import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, ChevronDown, Search, X } from "lucide-react";
import { jsPDF } from "jspdf";
import { uploadFirmwareFile, patchFirmwareMetadata, getBaseline, getFirmwareFileInfo, getFirmwareBinUrl, getFirmwareReleaseNotesUrl, getAuthToken, getFirmwarePublishDetails, finalPublishFirmware, approveBaseline } from "../../lib/api";
import { formatTargetCountries } from "../../lib/utils";
import { inspectFirmware } from "../../lib/mcuboot";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";

// Must match MAX_FILE_SIZE in services/firmware-management/upload-firmware-lambda —
// the API rejects anything larger, so rejecting here avoids a pointless round-trip.
// 390 KB, not 400: the binary must fit inside one 400 KB DynamoDB record together
// with the rest of the baseline's fields (FMP-UP-002, SRS rev 17; DISC-025).
const MAX_BYTES = 390 * 1024;
const MAX_LABEL = `${MAX_BYTES / 1024} KB`;

// ISO 3166-1 alpha-2 country list
const COUNTRIES = [
  { code: "AF", label: "Afghanistan" },
  { code: "AL", label: "Albania" },
  { code: "DZ", label: "Algeria" },
  { code: "AD", label: "Andorra" },
  { code: "AO", label: "Angola" },
  { code: "AG", label: "Antigua and Barbuda" },
  { code: "AR", label: "Argentina" },
  { code: "AM", label: "Armenia" },
  { code: "AU", label: "Australia" },
  { code: "AT", label: "Austria" },
  { code: "AZ", label: "Azerbaijan" },
  { code: "BS", label: "Bahamas" },
  { code: "BH", label: "Bahrain" },
  { code: "BD", label: "Bangladesh" },
  { code: "BB", label: "Barbados" },
  { code: "BY", label: "Belarus" },
  { code: "BE", label: "Belgium" },
  { code: "BZ", label: "Belize" },
  { code: "BJ", label: "Benin" },
  { code: "BT", label: "Bhutan" },
  { code: "BO", label: "Bolivia" },
  { code: "BA", label: "Bosnia and Herzegovina" },
  { code: "BW", label: "Botswana" },
  { code: "BR", label: "Brazil" },
  { code: "BN", label: "Brunei" },
  { code: "BG", label: "Bulgaria" },
  { code: "BF", label: "Burkina Faso" },
  { code: "BI", label: "Burundi" },
  { code: "CV", label: "Cabo Verde" },
  { code: "KH", label: "Cambodia" },
  { code: "CM", label: "Cameroon" },
  { code: "CA", label: "Canada" },
  { code: "CF", label: "Central African Republic" },
  { code: "TD", label: "Chad" },
  { code: "CL", label: "Chile" },
  { code: "CN", label: "China" },
  { code: "CO", label: "Colombia" },
  { code: "KM", label: "Comoros" },
  { code: "CG", label: "Congo" },
  { code: "CD", label: "Congo (DRC)" },
  { code: "CR", label: "Costa Rica" },
  { code: "CI", label: "Côte d'Ivoire" },
  { code: "HR", label: "Croatia" },
  { code: "CU", label: "Cuba" },
  { code: "CY", label: "Cyprus" },
  { code: "CZ", label: "Czechia" },
  { code: "DK", label: "Denmark" },
  { code: "DJ", label: "Djibouti" },
  { code: "DM", label: "Dominica" },
  { code: "DO", label: "Dominican Republic" },
  { code: "EC", label: "Ecuador" },
  { code: "EG", label: "Egypt" },
  { code: "SV", label: "El Salvador" },
  { code: "GQ", label: "Equatorial Guinea" },
  { code: "ER", label: "Eritrea" },
  { code: "EE", label: "Estonia" },
  { code: "SZ", label: "Eswatini" },
  { code: "ET", label: "Ethiopia" },
  { code: "FJ", label: "Fiji" },
  { code: "FI", label: "Finland" },
  { code: "FR", label: "France" },
  { code: "GA", label: "Gabon" },
  { code: "GM", label: "Gambia" },
  { code: "GE", label: "Georgia" },
  { code: "DE", label: "Germany" },
  { code: "GH", label: "Ghana" },
  { code: "GR", label: "Greece" },
  { code: "GD", label: "Grenada" },
  { code: "GT", label: "Guatemala" },
  { code: "GN", label: "Guinea" },
  { code: "GW", label: "Guinea-Bissau" },
  { code: "GY", label: "Guyana" },
  { code: "HT", label: "Haiti" },
  { code: "HN", label: "Honduras" },
  { code: "HU", label: "Hungary" },
  { code: "IS", label: "Iceland" },
  { code: "IN", label: "India" },
  { code: "ID", label: "Indonesia" },
  { code: "IR", label: "Iran" },
  { code: "IQ", label: "Iraq" },
  { code: "IE", label: "Ireland" },
  { code: "IL", label: "Israel" },
  { code: "IT", label: "Italy" },
  { code: "JM", label: "Jamaica" },
  { code: "JP", label: "Japan" },
  { code: "JO", label: "Jordan" },
  { code: "KZ", label: "Kazakhstan" },
  { code: "KE", label: "Kenya" },
  { code: "KI", label: "Kiribati" },
  { code: "KW", label: "Kuwait" },
  { code: "KG", label: "Kyrgyzstan" },
  { code: "LA", label: "Laos" },
  { code: "LV", label: "Latvia" },
  { code: "LB", label: "Lebanon" },
  { code: "LS", label: "Lesotho" },
  { code: "LR", label: "Liberia" },
  { code: "LY", label: "Libya" },
  { code: "LI", label: "Liechtenstein" },
  { code: "LT", label: "Lithuania" },
  { code: "LU", label: "Luxembourg" },
  { code: "MG", label: "Madagascar" },
  { code: "MW", label: "Malawi" },
  { code: "MY", label: "Malaysia" },
  { code: "MV", label: "Maldives" },
  { code: "ML", label: "Mali" },
  { code: "MT", label: "Malta" },
  { code: "MH", label: "Marshall Islands" },
  { code: "MR", label: "Mauritania" },
  { code: "MU", label: "Mauritius" },
  { code: "MX", label: "Mexico" },
  { code: "FM", label: "Micronesia" },
  { code: "MD", label: "Moldova" },
  { code: "MC", label: "Monaco" },
  { code: "MN", label: "Mongolia" },
  { code: "ME", label: "Montenegro" },
  { code: "MA", label: "Morocco" },
  { code: "MZ", label: "Mozambique" },
  { code: "MM", label: "Myanmar" },
  { code: "NA", label: "Namibia" },
  { code: "NR", label: "Nauru" },
  { code: "NP", label: "Nepal" },
  { code: "NL", label: "Netherlands" },
  { code: "NZ", label: "New Zealand" },
  { code: "NI", label: "Nicaragua" },
  { code: "NE", label: "Niger" },
  { code: "NG", label: "Nigeria" },
  { code: "NO", label: "Norway" },
  { code: "OM", label: "Oman" },
  { code: "PK", label: "Pakistan" },
  { code: "PW", label: "Palau" },
  { code: "PA", label: "Panama" },
  { code: "PG", label: "Papua New Guinea" },
  { code: "PY", label: "Paraguay" },
  { code: "PE", label: "Peru" },
  { code: "PH", label: "Philippines" },
  { code: "PL", label: "Poland" },
  { code: "PT", label: "Portugal" },
  { code: "QA", label: "Qatar" },
  { code: "RO", label: "Romania" },
  { code: "RU", label: "Russia" },
  { code: "RW", label: "Rwanda" },
  { code: "KN", label: "Saint Kitts and Nevis" },
  { code: "LC", label: "Saint Lucia" },
  { code: "VC", label: "Saint Vincent and the Grenadines" },
  { code: "WS", label: "Samoa" },
  { code: "SM", label: "San Marino" },
  { code: "ST", label: "São Tomé and Príncipe" },
  { code: "SA", label: "Saudi Arabia" },
  { code: "SN", label: "Senegal" },
  { code: "RS", label: "Serbia" },
  { code: "SC", label: "Seychelles" },
  { code: "SL", label: "Sierra Leone" },
  { code: "SG", label: "Singapore" },
  { code: "SK", label: "Slovakia" },
  { code: "SI", label: "Slovenia" },
  { code: "SB", label: "Solomon Islands" },
  { code: "SO", label: "Somalia" },
  { code: "ZA", label: "South Africa" },
  { code: "SS", label: "South Sudan" },
  { code: "ES", label: "Spain" },
  { code: "LK", label: "Sri Lanka" },
  { code: "SD", label: "Sudan" },
  { code: "SR", label: "Suriname" },
  { code: "SE", label: "Sweden" },
  { code: "CH", label: "Switzerland" },
  { code: "SY", label: "Syria" },
  { code: "TW", label: "Taiwan" },
  { code: "TJ", label: "Tajikistan" },
  { code: "TZ", label: "Tanzania" },
  { code: "TH", label: "Thailand" },
  { code: "TL", label: "Timor-Leste" },
  { code: "TG", label: "Togo" },
  { code: "TO", label: "Tonga" },
  { code: "TT", label: "Trinidad and Tobago" },
  { code: "TN", label: "Tunisia" },
  { code: "TR", label: "Turkey" },
  { code: "TM", label: "Turkmenistan" },
  { code: "TV", label: "Tuvalu" },
  { code: "UG", label: "Uganda" },
  { code: "UA", label: "Ukraine" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "UY", label: "Uruguay" },
  { code: "UZ", label: "Uzbekistan" },
  { code: "VU", label: "Vanuatu" },
  { code: "VE", label: "Venezuela" },
  { code: "VN", label: "Vietnam" },
  { code: "YE", label: "Yemen" },
  { code: "ZM", label: "Zambia" },
  { code: "ZW", label: "Zimbabwe" },
];

const COUNTRY_LABEL = Object.fromEntries(COUNTRIES.map(({ code, label }) => [code, label]));

// Role names as shown to the reviewer on the attestation line.
function reviewerRoleLabel(role) {
  if (role === "FleetAdmin") return "Fleet Admin";
  if (role === "ReleaseManager") return "Release Manager";
  return role || "";
}

// Tri-state flag rendering; `dash` is what an unknown value renders as.
function yesNoOr(value, dash) {
  if (value == null) return dash;
  return value ? "Yes" : "No";
}

// The rollout tier set at publish, with the share of the fleet it caps a rollout
// to. Records published before tiering shipped carry no tier.
function tierLabel(tier, dash = "—") {
  const labels = {
    canary: "Canary (up to 5%)",
    extended: "Extended (up to 25%)",
    full: "Full Fleet (up to 100%)",
  };
  return labels[String(tier || "").toLowerCase()] || dash;
}

// Epoch seconds, epoch milliseconds and ISO strings all appear in the API payloads.
function toEpochMs(v) {
  if (typeof v !== "number") return Date.parse(v);
  return v < 1e10 ? v * 1000 : v;
}

// Country dropdown trigger border: an error outranks the open state.
function countryTriggerBorderClass(error, open) {
  if (error) return "border-red-400";
  if (open) return "border-teal-500 ring-1 ring-teal-300";
  return "border-slate-200 hover:border-teal-300";
}

// Step bubble styling: current step navy, completed steps teal, the rest outlined.
function stepBubbleClass(stepNumber, current) {
  if (stepNumber === current) return "bg-[#08148C] text-white";
  if (stepNumber < current) return "bg-teal-600 text-white";
  return "border-2 border-slate-300 text-slate-400 bg-white";
}

// Review-row value styling; the flags are mutually exclusive in practice.
function reviewValueClass({ bold, teal, mono }) {
  if (bold) return "font-bold text-slate-800";
  if (teal) return "text-teal-600 font-medium";
  if (mono) return "font-mono text-slate-600 text-xs leading-5";
  return "text-slate-700";
}

// Review screen file size: the freshly picked File first, then the cached fileInfo.
function reviewFileSize(data) {
  if (data.file) return `${formatBytes(data.file.size)} (${data.file.size.toLocaleString()} bytes)`;
  if (data.fileInfo?.size) return `${formatBytes(data.fileInfo.size)} (${Number(data.fileInfo.size).toLocaleString()} bytes)`;
  return "—";
}

function releaseNoteViewLabel(isLoading, isViewing) {
  if (isLoading) return "...";
  if (isViewing) return "\u{1F441} Viewing";
  return "\u{1F441} View";
}

// Multi-select country dropdown component
function CountryDropdown({ value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = search.trim()
    ? COUNTRIES.filter((c) =>
        c.label.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : COUNTRIES;

  const toggle = (code) => {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  };

  const removeChip = (code, e) => {
    e.stopPropagation();
    onChange(value.filter((c) => c !== code));
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <div // NOSONAR S6848/S1082: this trigger contains a nested <button> (the ✕ remove-chip), so it cannot itself become a <button>; role="button" would only trade S6848 for S6819.
        onClick={() => setOpen((o) => !o)}
        className={`min-h-[42px] flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-lg border cursor-pointer bg-white transition-colors ${countryTriggerBorderClass(error, open)}`}
      >
        {value.length === 0 ? (
          <span className="text-sm text-slate-400">Select countries…</span>
        ) : (
          value.map((code) => (
            <span key={code} className="flex items-center gap-1 bg-teal-50 border border-teal-200 text-[#08148C] text-xs font-medium px-2 py-0.5 rounded-full">
              <span className="font-mono">{code}</span>
              <span className="text-teal-500">·</span>
              {COUNTRY_LABEL[code]}
              <button type="button" onClick={(e) => removeChip(code, e)} className="ml-0.5 text-teal-400 hover:text-[#08148C]">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code…"
              className="flex-1 text-sm outline-none bg-transparent placeholder-slate-400"
            />
            {search && <button type="button" onClick={() => setSearch("")}><X className="w-3.5 h-3.5 text-slate-400" /></button>}
          </div>

          {/* Select all / Clear */}
          <div className="flex gap-3 px-3 py-1.5 border-b border-slate-100 text-xs">
            <button type="button" className="text-teal-600 hover:underline" onClick={() => onChange(COUNTRIES.map((c) => c.code))}>Select all</button>
            <button type="button" className="text-slate-400 hover:underline" onClick={() => onChange([])}>Clear</button>
            <span className="ml-auto text-slate-400">{value.length} selected</span>
          </div>

          {/* List */}
          <ul className="max-h-52 overflow-y-auto divide-y divide-slate-50">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-slate-400 text-center">No countries found</li>
            )}
            {filtered.map(({ code, label }) => {
              const checked = value.includes(code);
              return (
                <li key={code}>
                  {/* Handler sits on a real <button> rather than the <li>: on the
                      list item it trips S6847, and role="button" would trip S6819.
                      Children are phrasing content, so the native tag is valid. */}
                  <button
                    type="button"
                    aria-pressed={checked}
                    onClick={() => toggle(code)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-2 cursor-pointer text-sm transition-colors ${
                      checked ? "bg-teal-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      checked ? "bg-teal-600 border-teal-600" : "border-slate-300"
                    }`}>
                      {checked && (
                        <svg viewBox="0 0 10 8" className="w-2.5 h-2" fill="none">
                          <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-400 w-7 shrink-0">{code}</span>
                    <span className={checked ? "font-medium text-[#08148C]" : "text-slate-700"}>{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes) {
  return (bytes / 1048576).toFixed(1) + " MB";
}

const STEPS = [
  { n: 1, label: "Upload" },
  { n: 2, label: "Metadata" },
  { n: 3, label: "Review / Sign" },
  { n: 4, label: "Publish" },
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${stepBubbleClass(s.n, current)}`}
            >
              {s.n < current ? <CheckCircle2 className="w-4 h-4" /> : s.n}
            </div>
            <span
              className={`text-sm whitespace-nowrap ${
                s.n === current ? "font-bold text-slate-800" : "text-slate-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className="flex-1 h-px bg-slate-200 mx-4 min-w-[40px]" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Results produced by a LATER gate (Review & Sign). Never carried back through
// Step 1: re-entering the wizard from the upload step must re-validate each gate
// rather than trust an approval recorded against the earlier pass.
const LATER_GATE_FIELDS = ["approvalSignature", "publishDetails"];

// What Step 1's "Next" should leave in wizardData (FMP-NV-017 / DISC-057).
// StepUpload only knows the upload fields, and wizardData used to be REPLACED
// with them - so Back from Step 2 to Step 1 and Next again silently emptied the
// version, notes and targets already entered on Step 2. For the SAME binary the
// entered state is kept; for a different binary it is reset as before, so
// metadata typed for one file can never attach itself to another.
// The fields StepMetadata autosaves to the draft as the user types. Back from
// Step 2 only changes the step, so anything typed since Step 2 was opened lives
// in the draft, not yet in wizardData.
const STEP2_FIELDS = ["version", "minFw", "mandatory", "segment", "countries", "reviewNoteFileNames",
  "cachedReleaseNotesFiles", "release_note_description"];

function mergeUploadIntoWizard(prev, upload, autosavedDraft) {
  const sameBinary = !!prev?.sha256 && prev.sha256 === upload?.sha256;
  if (!sameBinary) return upload;
  const kept = { ...prev };
  if (autosavedDraft && autosavedDraft.sha256 === prev.sha256) {
    STEP2_FIELDS.forEach((k) => { if (autosavedDraft[k] !== undefined) kept[k] = autosavedDraft[k]; });
  }
  LATER_GATE_FIELDS.forEach((k) => delete kept[k]);
  // A remounted StepUpload has no File object after navigation and has not
  // re-parsed the image, so it reports those as null; keep what was known.
  const fresh = Object.fromEntries(
    Object.entries(upload).filter(([, v]) => v !== null && v !== undefined)
  );
  return { ...kept, ...fresh };
}

function StepUpload({ onNext, initialFile, initialSha256, initialFileInfo, initialFirmwareId, initialBuildDate, initialBuildTime }) {
  const { user } = useAuth();
  const inputRef = useRef();
  const firmwareIdRef = useRef(initialFirmwareId || null);
  // Failed attempts of the binary on screen, carried into the next try (Retry
  // Upload or re-selecting it) so the service records the fault with the recovery
  // (TP-FMP-02-TS-02-TC-0013 FT-006). Keyed by SHA-256: a different file starts clean.
  const transferFailuresRef = useRef({ sha256: null, list: [] });
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(initialFile || null);
  const [sha256, setSha256] = useState(initialSha256 || null);
  const [computing, setComputing] = useState(false);
  // MCUboot header/TLV facts read off the uploaded image, surfaced at Review.
  const [fwInfo, setFwInfo] = useState(null);
  const [readProgress, setReadProgress] = useState(0);
  const [, setProgressLabel] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(!!initialSha256);
  const [uploadError, setUploadError] = useState(null);
  const [retryInfo, setRetryInfo] = useState(null);
  // True when the backend matched this binary to a draft uploaded moments ago and
  // returned that record instead of creating one. Kept separate from retryInfo,
  // which holds { attempt, maxAttempts } for the network-retry banner.
  const [reusedExisting, setReusedExisting] = useState(false);
  const [error, setError] = useState(null);
  // Build date / time — default to today
  const todayDate = new Date().toISOString().slice(0, 10);
  const nowTime = new Date().toTimeString().slice(0, 5); // HH:MM
  const [buildDate, setBuildDate] = useState(initialBuildDate || todayDate);
  const [buildTime, setBuildTime] = useState(initialBuildTime || nowTime);
  // Restored file display info when File object is unavailable after navigation
  const restoredFileInfo = initialFileInfo || null;

  // Upload only — kept separate from processFile so a retry re-sends the bytes
  // without re-validating, re-parsing the filename or recomputing SHA-256.
  const runUpload = useCallback(async (f, { buildDate: bd, buildTime: bt, sha256: hash }) => {
    setUploadError(null);
    setRetryInfo(null);
    setReusedExisting(false);
    setComputing(true);
    setUploadProgress(0);
    setProgressLabel("Uploading...");
    await new Promise((r) => setTimeout(r, 30));
    const prior = transferFailuresRef.current.sha256 === hash ? transferFailuresRef.current.list : [];
    try {
      const uploadResponse = await uploadFirmwareFile({
        file: f,
        fileName: f.name,
        onProgress: (pct) => setUploadProgress(pct),
        onRetry: ({ attempt, maxAttempts }) => setRetryInfo({ attempt, maxAttempts }),
        name:  user?.name  || user?.full_name  || null,
        email: user?.email || null,
        buildDate: bd,
        buildTime: bt,
        sha256: hash,
        priorFailures: prior,
      });
      transferFailuresRef.current = { sha256: null, list: [] };   // reported with this upload
      firmwareIdRef.current = uploadResponse?.id || uploadResponse?.firmware_id || null;
      // The backend dedupes a retried upload of the same binary and returns the
      // existing record. Surface that, otherwise reusing a draft is indistinguishable
      // from creating one and a missing new draft looks like a failure.
      setReusedExisting(!!uploadResponse?.deduped);
      setUploadProgress(100);
      setUploadDone(true);
      setRetryInfo(null);
    } catch (err) {
      if (Array.isArray(err.transferFailures)) {
        transferFailuresRef.current = { sha256: hash, list: err.transferFailures };
      }
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setComputing(false);
    }
  }, [user?.email, user?.full_name, user?.name]);

  const handleRetryUpload = useCallback(() => {
    if (!file || !sha256) return;
    setReadProgress(100);
    runUpload(file, { buildDate, buildTime, sha256 });
  }, [file, sha256, buildDate, buildTime, runUpload]);

  const processFile = useCallback(async (f) => {
    setError(null);
    setUploadError(null);
    setFwInfo(null);
    if (!f.name.toLowerCase().endsWith(".bin")) {
      setError("Invalid file type. Only .bin files are accepted.");
      setFile(null);
      setSha256(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`File exceeds the ${MAX_LABEL} limit.`);
      setFile(null);
      setSha256(null);
      return;
    }
    // Try to parse build date/time from filename like ..._YYYYMMDD_HHMM
    let parsedDate = null;
    let parsedTime = null;
    try {
      const m = f.name.match(/_(\d{8})_(\d{4})(?:\.|$)/);
      if (m) {
        const datePart = m[1];
        const timePart = m[2];
        parsedDate = `${datePart.slice(0,4)}-${datePart.slice(4,6)}-${datePart.slice(6,8)}`;
        parsedTime = `${timePart.slice(0,2)}:${timePart.slice(2,4)}`;
        setBuildDate(parsedDate);
        setBuildTime(parsedTime);
      }
    } catch (err) {
      // ignore parse errors
    }

    setFile(f);
    setComputing(true);
    setUploadDone(false);
    setReadProgress(0);
    setProgressLabel("Reading file...");

    await new Promise((r) => setTimeout(r, 50));

    // Phase 1: Time-based read progress (0 → 100)
    const DURATION_MS = 2000;
    const INTERVAL_MS = 40;
    const steps = DURATION_MS / INTERVAL_MS;
    let step = 0;
    const ticker = setInterval(() => {
      step += 1;
      const pct = Math.min(Math.round((step / steps) * 90), 90);
      setReadProgress(pct);
      if (step >= steps) clearInterval(ticker);
    }, INTERVAL_MS);

    const buffer = await f.arrayBuffer();
    clearInterval(ticker);

    // Reject anything that is not a signed MCUboot image before hashing or
    // uploading it — the signature is a TLV record in the image trailer, so this
    // needs no key material and no network call.
    setProgressLabel("Verifying signature...");
    const info = inspectFirmware(buffer);
    setFwInfo(info);
    if (!info.valid) {
      setError(info.reason);
      setFile(null);
      setSha256(null);
      setComputing(false);
      setReadProgress(0);
      setProgressLabel("");
      return;
    }

    setReadProgress(95);
    setProgressLabel("Computing SHA-256...");
    await new Promise((r) => setTimeout(r, 30));

    const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
    const hash = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    setReadProgress(100);
    setSha256(hash);

    // Phase 2: Upload file to API
    await runUpload(f, {
      buildDate: parsedDate || buildDate,
      buildTime: parsedTime || buildTime,
      sha256: hash,
    });
  }, [buildDate, buildTime, runUpload]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  };

  const uploadedAt = file
    ? new Date().toISOString().replace("T", " ").slice(0, 19) + " UTC"
    : null;

  return (
    <div className="space-y-5">
      <div // NOSONAR S6848: drag-and-drop zone — <button> conflicts with drop semantics, and role="button" only trades S6848 for S6819.
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed cursor-pointer transition-colors py-16 px-8 flex flex-col items-center justify-center gap-3 ${
          dragOver ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-slate-50/60 hover:border-teal-300"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".bin"
          className="hidden"
          onChange={(e) => { if (e.target.files[0]) processFile(e.target.files[0]); }}
        />
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-xl bg-indigo-100 flex items-end justify-center pb-1">
            <svg viewBox="0 0 40 32" className="w-10 h-8" fill="none">
              <rect x="2" y="14" width="36" height="16" rx="3" fill="#818cf8" />
              <rect x="8" y="18" width="24" height="8" rx="2" fill="#c7d2fe" />
            </svg>
          </div>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
            <svg viewBox="0 0 24 28" className="w-6 h-7" fill="none">
              <path d="M12 26V6" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
              <path d="M5 13l7-7 7 7" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <p className="text-base font-bold text-slate-700">Drag &amp; drop firmware file here</p>
        <p className="text-sm text-slate-400">or click to browse</p>
        <p className="text-sm font-medium text-teal-600">.bin files only &middot; Max {MAX_LABEL}</p>
      </div>

        {/* Build Date / Time */}
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 space-y-3">
          <p className="text-sm font-semibold text-slate-700">Build Date &amp; Time</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
              <label htmlFor="upload-build-date" className="text-xs font-medium text-slate-500">Build Date</label>
              <input
                id="upload-build-date"
                type="date"
                value={buildDate}
                onChange={(e) => setBuildDate(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
              />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
              <label htmlFor="upload-build-time" className="text-xs font-medium text-slate-500">Build Time (UTC)</label>
              <input
                id="upload-build-time"
                type="time"
                value={buildTime}
                onChange={(e) => setBuildTime(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
              />
            </div>
          </div>
        </div>

      {computing && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">
              {retryInfo
                ? `Connection lost — retrying (attempt ${retryInfo.attempt} of ${retryInfo.maxAttempts})...`
                : "Uploading..."}
            </p>
            <p className="text-sm font-bold text-indigo-600">
              {readProgress < 100 ? "Preparing..." : `${uploadProgress}%`}
            </p>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-2.5 bg-indigo-500 rounded-full transition-all duration-300"
              style={{ width: readProgress < 100 ? "0%" : `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-slate-400">Do not close this window.</p>
        </div>
      )}

      {uploadError && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-700">Upload Failed</p>
            <p className="text-xs text-red-500 mt-0.5">{uploadError}</p>
          </div>
          {file && sha256 && (
            <button
              type="button"
              onClick={handleRetryUpload}
              disabled={computing}
              className="shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Retry Upload
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">Upload Rejected</p>
            <p className="text-xs text-red-500 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {(file || restoredFileInfo) && (
        <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-2xl shrink-0">
            📦
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 text-sm truncate">{file ? file.name : restoredFileInfo.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {formatBytes(file ? file.size : restoredFileInfo.size)} &middot; Uploaded {uploadedAt}
            </p>
            <p className="text-xs font-semibold text-teal-600 mt-2 mb-1">SHA-256</p>
            {computing ? (
              <p className="text-xs text-slate-400 italic">Computing...</p>
            ) : (
              <input
                readOnly
                value={sha256 || ""}
                className="w-full text-xs font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-slate-600 select-all"
              />
            )}
          </div>
          {sha256 && !computing && (
            <span className="text-emerald-500 text-xl mt-1 shrink-0">&#x2713;</span>
          )}
        </div>
      )}

      {sha256 && !computing && uploadDone && (
        <div className={`flex items-start gap-3 border rounded-xl px-4 py-3 ${
          reusedExisting ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
        }`}>
          <CheckCircle2 className={`w-5 h-5 mt-0.5 shrink-0 ${
            reusedExisting ? "text-amber-500" : "text-emerald-500"
          }`} />
          <div>
            {reusedExisting ? (
              <>
                <p className="text-sm font-semibold text-slate-800">Existing draft resumed &middot; SHA-256 verified</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  This binary matches a draft uploaded moments ago, so that draft was reused instead of
                  creating a new one. Any metadata you enter will update it.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-slate-800">File uploaded &middot; SHA-256 verified</p>
                <p className="text-xs text-slate-500 mt-0.5">File successfully uploaded to server. Proceed to metadata entry.</p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          disabled={(!sha256 && !restoredFileInfo) || computing || (!uploadDone && !restoredFileInfo)}
          onClick={() => onNext({ file, sha256, uploadedAt, firmwareId: firmwareIdRef.current, fileInfo: file ? { name: file.name, size: file.size } : restoredFileInfo, buildDate, buildTime, fwInfo })}
          className="flex items-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          Next: Add Metadata
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function StepMetadata({ uploadData, initialData, onNext, onBack, onAutosave }) {
  const reviewNotesInputRef = useRef();
  const [version, setVersion] = useState(initialData?.version || "");
  const [minFw, setMinFw] = useState(initialData?.minFw || "");
  const [reviewNoteFiles, setReviewNoteFiles] = useState([]);
  // saveDraft now persists base64 file content so files can be fully restored on resume.
  // Names without backing base64 (old drafts / very fast navigation) are "stale" — shown with an amber badge.
  const hasCachedFiles = (initialData?.cachedReleaseNotesFiles?.length ?? 0) > 0;
  // Mutable set so we can clear names as the user re-uploads each file.
  const [staleDraftNames, setStaleDraftNames] = useState(() =>
    !hasCachedFiles ? new Set(initialData?.reviewNoteFileNames ?? []) : new Set()
  );
  // Always restore names from draft — stale ones are displayed with an amber re-upload badge.
  const [reviewNoteFileNames, setReviewNoteFileNames] = useState(
    initialData?.reviewNoteFileNames ?? []
  );
  const [releaseNoteDescription, setReleaseNoteDescription] = useState(initialData?.release_note_description || "");
  // Base64-encoded file content — restored from draft or built as the user adds files.
  const [localCachedFiles, setLocalCachedFiles] = useState(
    initialData?.cachedReleaseNotesFiles ?? []
  );
  const [mandatory, setMandatory] = useState(initialData?.mandatory ?? false);
  const [segment, setSegment] = useState(initialData?.segment || "");
  const [countries, setCountries] = useState(initialData?.countries || []);
  const [versionError, setVersionError] = useState(null);
  const [minFwError, setMinFwError] = useState(null);
  const [reviewNotesError, setReviewNotesError] = useState(null);
  const [countriesError, setCountriesError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Keep a ref to the latest onAutosave so the effect below never stales
  const onAutosaveRef = useRef(onAutosave);
  useEffect(() => { onAutosaveRef.current = onAutosave; });

  // Auto-save draft to localStorage as the user types (debounced 600 ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      onAutosaveRef.current?.({
        version,
        minFw,
        mandatory,
        segment,
        countries,
        reviewNoteFileNames,
        cachedReleaseNotesFiles: localCachedFiles,
        release_note_description: releaseNoteDescription,
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [version, minFw, mandatory, segment, countries, reviewNoteFileNames, localCachedFiles, releaseNoteDescription]);

  // Flush current field values immediately when the user navigates away or closes the tab.
  // This ensures data typed within the 600 ms debounce window is never lost.
  useEffect(() => {
    const handleUnload = () => {
      onAutosaveRef.current?.({ version, minFw, mandatory, segment, countries, reviewNoteFileNames, cachedReleaseNotesFiles: localCachedFiles, release_note_description: releaseNoteDescription });
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [version, minFw, mandatory, segment, countries, reviewNoteFileNames, localCachedFiles]);
  const { toast } = useToast();
  // Failures are raised only as a toast. They previously also rendered as a red
  // line wedged between Back and Next, which repeated what the toast already
  // said and pushed the two buttons apart as the text wrapped.
  const failWith = (message) => {
    toast({ title: "Could not save metadata", description: message, variant: "destructive" });
  };
  const [uploadedFileName, setUploadedFileName] = useState(uploadData?.file?.name || uploadData?.fileInfo?.name || null);
  const [serverNoteNames, setServerNoteNames] = useState([]);

  useEffect(() => {
    if (!uploadData?.firmwareId) return;
    getFirmwareFileInfo(uploadData.firmwareId)
      .then((info) => {
        const name = info?.file_name || info?.fileName || info?.name || null;
        if (name) setUploadedFileName(name);
      })
      .catch(() => {});
  }, [uploadData?.firmwareId]);

  // Fetch server-side file names whenever the firmware ID is available.
  // Also covers draft-resume where reviewNoteFileNames was cleared (stale after navigation).
  useEffect(() => {
    if (!uploadData?.firmwareId) return;
    getBaseline(uploadData.firmwareId)
      .then((res) => {
        const raw = res?.data ?? res?.firmware ?? res ?? {};
        const notes = raw.release_notes_files ?? raw.review_notes_files ?? raw.review_notes ?? raw.release_notes ?? raw.notes_files ?? raw.attachments ?? [];
        let names = [];
        if (Array.isArray(notes)) {
          names = notes.map((n) => (typeof n === "object" ? (n.file_name || n.name || n.filename) : n)).filter(Boolean);
        } else if (typeof notes === "string" && notes) {
          names = [notes];
        }
        if (names.length > 0) setServerNoteNames(names);
      })
      .catch(() => {});
  }, [uploadData?.firmwareId, initialData?.reviewNoteFileNames]);

  const allFileNames = reviewNoteFileNames;

  const ALLOWED_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
  ];
  const ALLOWED_EXTS = [".pdf", ".docx", ".txt"];

  const isAllowed = (file) =>
    ALLOWED_TYPES.includes(file.type) ||
    ALLOWED_EXTS.some((ext) => file.name.toLowerCase().endsWith(ext));

  const addReviewNoteFiles = async (incoming) => {
    const allFiles = Array.from(incoming);
    const rejected = allFiles.filter((f) => !isAllowed(f));
    // Allow re-uploading stale files (same name, no backing File object yet) in addition to new files.
    const valid = allFiles.filter((f) => isAllowed(f) && (!reviewNoteFileNames.includes(f.name) || staleDraftNames.has(f.name)));
    if (rejected.length > 0) {
      setReviewNotesError(`Only PDF, DOCX and TXT files are allowed. Rejected: ${rejected.map((f) => f.name).join(", ")}`);
    } else {
      setReviewNotesError(null);
    }
    if (valid.length === 0) return;
    const uploadedNames = valid.map((f) => f.name);
    // Replace stale File entry (if any) and add truly new ones.
    setReviewNoteFiles((prev) => [...prev.filter((f) => !uploadedNames.includes(f.name)), ...valid]);
    // Resolve stale names that have now been re-uploaded.
    setStaleDraftNames((prev) => { const next = new Set(prev); uploadedNames.forEach((n) => next.delete(n)); return next; });
    // Only append names that aren't already in the list (stale names stay in place).
    setReviewNoteFileNames((prev) => {
      const newNames = uploadedNames.filter((n) => !prev.includes(n));
      return newNames.length > 0 ? [...prev, ...newNames] : prev;
    });
    // Convert to base64 immediately so the content is persisted in the draft.
    try {
      const base64Entries = await Promise.all(
        valid.map((f) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve({ file_name: f.name, file_data: reader.result.split(",")[1] });
            reader.onerror = reject;
            reader.readAsDataURL(f);
          })
        )
      );
      setLocalCachedFiles((prev) => {
        const without = prev.filter((c) => !uploadedNames.includes(c.file_name));
        return [...without, ...base64Entries];
      });
    } catch (_) {}
  };

  const removeReviewNoteFile = (index) => {
    const nameToRemove = reviewNoteFileNames[index];
    setReviewNoteFiles((prev) => prev.filter((f) => f.name !== nameToRemove));
    setStaleDraftNames((prev) => { const next = new Set(prev); next.delete(nameToRemove); return next; });
    setLocalCachedFiles((prev) => prev.filter((c) => c.file_name !== nameToRemove));
    setReviewNoteFileNames((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    let ok = true;
    if (!/^v?\d+\.\d+\.\d+\.\d+(-[\w.]+)?$/.test(version.trim())) {
      setVersionError("Use format: vMAJOR.MINOR.PATCH.BUILD, e.g. v2.0.10.34 or v2.0.10.34-beta");
      ok = false;
    } else setVersionError(null);
    // Optional, but when given it must be a device version - the service refuses
    // anything else (metadata-firmware-lambda _min_device_fw, DISC-032).
    if (minFw.trim() && !/^[vV]?\d+\.\d+\.\d+(\.\d+)?$/.test(minFw.trim())) {
      setMinFwError("Use a version such as v2.0.44.70 or 2.0.0 - or leave it empty.");
      ok = false;
    } else setMinFwError(null);
    const unresolvedStale = [...staleDraftNames].filter((n) => reviewNoteFileNames.includes(n));
    if (allFileNames.length === 0 && serverNoteNames.length === 0) {
      setReviewNotesError("Upload at least one review note file.");
      ok = false;
    } else if (unresolvedStale.length > 0) {
      setReviewNotesError("Re-upload the highlighted files before continuing.");
      ok = false;
    } else setReviewNotesError(null);
    if (countries.length === 0) {
      setCountriesError("Select at least one target country.");
      ok = false;
    } else setCountriesError(null);
    return ok;
  };

  return (
    <div className="space-y-5">
      {/* Uploaded file banner */}
      {uploadedFileName && (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-0.5">Uploaded Firmware File</p>
            <p className="text-sm font-semibold text-slate-800 truncate">
              {uploadedFileName}
            </p>
          </div>
        </div>
      )}

      {/* White card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">

        {/* Row 1: Version String + Min FW */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label htmlFor="upload-version-string" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Version String <span className="text-red-500">*</span>
            </label>
            <input
              id="upload-version-string"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="Format: vX.Y.Z.B or vX.Y.Z.B-label"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {versionError
              ? <p className="text-xs text-red-500 mt-1">{versionError}</p>
              : <p className="text-xs text-slate-400 mt-1">Format: vMAJOR.MINOR.PATCH.BUILD &middot; must be unique &middot; e.g. v2.0.10.34</p>
            }
          </div>
          <div>
            <label htmlFor="upload-min-device-fw" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Minimum Device FW Required
            </label>
            <input
              id="upload-min-device-fw"
              value={minFw}
              onChange={(e) => setMinFw(e.target.value)}
              placeholder="Format: vX.Y.Z.B"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {minFwError
              ? <p className="text-xs text-red-500 mt-1">{minFwError}</p>
              : <p className="text-xs text-slate-400 mt-1">Devices below this version cannot receive update</p>
            }
          </div>
        </div>

        {/* Review Notes Upload */}
        <div>
          <label htmlFor="upload-release-notes" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Release Notes <span className="text-red-500">*</span>
          </label>
          <div // NOSONAR S6848: drag-and-drop zone — <button> conflicts with drop semantics, and role="button" only trades S6848 for S6819.
            onClick={() => reviewNotesInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addReviewNoteFiles(e.dataTransfer.files); }}
            className="border-2 border-dashed border-slate-200 rounded-xl px-5 py-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
          >
            <input
              id="upload-release-notes"
              ref={reviewNotesInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              className="hidden"
              onChange={(e) => {
                if (e.target.files.length) {
                  addReviewNoteFiles(e.target.files);
                }
                // Cleared unconditionally and deliberately: it lets the same
                // file be picked again and still fire onChange (S2681).
                e.target.value = "";
              }}
            />
            <svg className="w-7 h-7 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-slate-500">Drag &amp; drop files or <span className="text-teal-600 font-semibold">click to browse</span></p>
            <p className="text-xs text-slate-400">PDF, DOCX, TXT &middot; Multiple files allowed</p>
          </div>
          {allFileNames.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {allFileNames.map((name, i) => {
                const isStale = staleDraftNames.has(name);
                return (
                  <li key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className={`flex items-center justify-between border rounded-lg px-3 py-2 ${
                    isStale ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"
                  }`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {isStale
                        ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                        : <svg className="w-4 h-4 text-teal-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                      }
                      <span className={`text-sm truncate ${isStale ? "text-amber-700" : "text-slate-700"}`}>{name}</span>
                      {isStale && <span className="ml-1 text-xs font-medium text-amber-500 shrink-0">re-upload required</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeReviewNoteFile(i)}
                      className="text-slate-400 hover:text-red-500 ml-3 shrink-0 transition-colors"
                    >
                      &times;
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-3">
            <label htmlFor="upload-release-note-description" className="block text-sm font-semibold text-slate-700 mb-1.5">Release Note Description</label>
            <textarea
              id="upload-release-note-description"
              value={releaseNoteDescription}
              onChange={(e) => setReleaseNoteDescription(e.target.value)}
              placeholder="Short summary of the release notes"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
              rows={3}
            />
            <p className="text-xs text-slate-400 mt-1">Optional — will be sent as <code>release_note_description</code> in the API payload.</p>
          </div>
          {serverNoteNames.length > 0 && allFileNames.length === 0 && (
            <div className="mt-2">
              <p className="text-xs text-slate-400 mb-1.5">Previously uploaded &mdash; stored on server. Upload new files to replace:</p>
              <ul className="space-y-1.5">
                {serverNoteNames.map((name, i) => (
                  <li key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-slate-600 truncate">{name}</span>
                    <span className="ml-auto text-xs text-blue-400 shrink-0">on server</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {reviewNotesError && <p className="text-xs text-red-500 mt-1.5">{reviewNotesError}</p>}
        </div>

        {/* Row 2: Update Type + Target Segment */}
        <div className="grid grid-cols-2 gap-5">
          {/* Labelled as a group: the control is a toggle button, not a form field. */}
          <fieldset className="min-w-0" aria-labelledby="upload-update-type-label">
            <p id="upload-update-type-label" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Update Type <span className="text-red-500">*</span>
            </p>
            <div className="border border-slate-200 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Mandatory Update</p>
                  <p className="text-xs text-slate-400">Forces update; all eligible devices required</p>
                </div>
                {/* Toggle */}
                <button
                  type="button"
                  onClick={() => setMandatory(!mandatory)}
                  className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ml-3 ${mandatory ? "bg-teal-600" : "bg-slate-200"}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 shadow transition-transform ${mandatory ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Optional &mdash; clinician-initiated via Mobile App</p>
          </fieldset>
          <div>
            <label htmlFor="upload-target-segment" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Target Segment
            </label>
            <input
              id="upload-target-segment"
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              placeholder="Format: segment name or rollout phase"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-slate-400 mt-1">Segment can be expanded after publish</p>
          </div>
        </div>

        {/* Target Countries */}
        {/* CountryDropdown is a custom composite widget, so this is a group label. */}
        <fieldset className="min-w-0" aria-labelledby="upload-target-countries-label">
          <p id="upload-target-countries-label" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Target Countries <span className="text-red-500">*</span>
          </p>
          <CountryDropdown
            value={countries}
            onChange={(val) => { setCountries(val); setCountriesError(null); }}
            error={!!countriesError}
          />
          {countriesError
            ? <p className="text-xs text-red-500 mt-1.5">{countriesError}</p>
            : <p className="text-xs text-slate-400 mt-1.5">
                {countriesHelpText(countries)}
              </p>
          }
        </fieldset>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        <button
          onClick={onBack}
          disabled={submitting}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2.5 bg-white transition-colors disabled:opacity-40"
        >
          &#8592; Back
        </button>
        <button
          disabled={submitting}
          onClick={async () => {
            if (!validate()) return;
            if (!uploadData?.firmwareId) {
              failWith("Firmware ID is missing. Please go back to Step 1 and re-upload the file.");
              return;
            }
            setSubmitting(true);
            try {
              // Convert each review note File to base64
              const release_notes_files = await Promise.all(
                reviewNoteFiles.map((f) =>
                  new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve({
                      file_name: f.name,
                      file_data: reader.result.split(",")[1], // strip data:...;base64,
                    });
                    reader.onerror = reject;
                    reader.readAsDataURL(f);
                  })
                )
              );
              // Use freshly-converted files, or fall back to cached base64 from a previous pass,
              // or finally fall back to server-side file name references (files already uploaded).
              const cachedReleaseNotesFiles = localCachedFiles.length > 0 ? localCachedFiles : (initialData?.cachedReleaseNotesFiles || []);
              const serverFileRefs = serverNoteNames.map((name) => ({ file_name: name }));
              let filesToSend = serverFileRefs;
              if (release_notes_files.length > 0) {
                filesToSend = release_notes_files;
              } else if (cachedReleaseNotesFiles.length > 0) {
                filesToSend = cachedReleaseNotesFiles;
              }
              const metaPayload = {
                version_string: version.trim(),
                min_device_fw_required: minFw || null,
                is_mandatory: mandatory,
                target_segment: segment || null,
                target_countries: countries.map((code) => ({ code, name: COUNTRY_LABEL[code] || code })),
                ...(releaseNoteDescription ? { release_note_description: releaseNoteDescription } : {}),
                ...(filesToSend.length > 0 && { release_notes_files: filesToSend }),
              };
              await patchFirmwareMetadata(uploadData.firmwareId, metaPayload);
              let firmwareRecord = null;
              try {
                firmwareRecord = await getBaseline(uploadData.firmwareId);
              } catch (_) {
                // GET failed — proceed without firmware record
              }
              // Persist the base64 file data in wizardData so it's available if the user navigates back
              const nextCachedFiles = release_notes_files.length > 0 ? release_notes_files : cachedReleaseNotesFiles;
              onNext({ ...uploadData, version: version.trim(), minFw, reviewNoteFileNames: allFileNames, mandatory, segment, countries, firmwareRecord, cachedReleaseNotesFiles: nextCachedFiles, release_note_description: releaseNoteDescription });
            } catch (err) {
              failWith(err.message || "Failed to save metadata. Please try again.");
            } finally {
              setSubmitting(false);
            }
          }}
          className="flex items-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          {submitting ? "Saving..." : "Next: Review & Sign"}
          {!submitting && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function StepReview({ data, onNext, onBack }) {
  const { user, role } = useAuth();
  const [artefactSigned, setArtefactSigned] = useState(null);   // { signed, signedSha256 } from the record
  const [confirmed, setConfirmed] = useState(false);
  // Seeded from the wizard payload so the recorded signature survives navigating
  // back from the publish step.
  const [signature, setSignature] = useState(data?.approvalSignature || null);
  const [downloading, setDownloading] = useState(false);
  const viewing = false;
  const [proceeding, setProceeding] = useState(false);
  const [proceedError, setProceedError] = useState(null);
  const [hexContent, setHexContent] = useState(null);
  const [rnLoading, setRnLoading] = useState(null);    // key of file being actioned
  const [rnViewUrl, setRnViewUrl] = useState(null);     // URL shown in side panel
  const [rnViewName, setRnViewName] = useState(null);   // filename shown in panel header
  const [rnViewIndex, setRnViewIndex] = useState(null); // index of currently viewed file
  const [uploadedFileName, setUploadedFileName] = useState(data?.file?.name || data?.fileInfo?.name || null);

  useEffect(() => {
    if (!data?.firmwareId) return;
    getFirmwareFileInfo(data.firmwareId)
      .then((info) => {
        const name = info?.file_name || info?.fileName || info?.name || null;
        if (name) setUploadedFileName(name);
      })
      .catch(() => {});
  }, [data?.firmwareId]);

  useEffect(() => {
    if (!data?.firmwareId) return;
    getBaseline(data.firmwareId)
      .then((res) => {
        const d = res?.data ?? res?.firmware ?? res ?? {};
        setArtefactSigned({ signed: d.signed === true, signedSha256: d.signed_sha256 || null });
      })
      .catch(() => {});
  }, [data?.firmwareId]);

  const fetchBinBlob = async () => {
    const token = await getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(getFirmwareBinUrl(data.firmwareId), { headers });
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.blob();
  };

  const handleDownloadBin = async () => {
    if (!data.firmwareId) return;
    setDownloading(true);
    try {
      const blob = await fetchBinBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.fileInfo?.name || data.file?.name || `firmware-${data.firmwareId}.bin`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[downloadBin]", err);
    } finally {
      setDownloading(false);
    }
  };

  const releaseNoteFiles = data.firmwareRecord?.release_notes_files || data.reviewNoteFileNames?.map((name) => ({ file_name: name })) || [];

  // Resolves the API metadata response to the actual presigned file URL.
  // The endpoint returns JSON like { release_notes: [{ file_name, download_url }] }.
  const resolveReleaseNoteUrl = async (index) => {
    const token = await getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const metaUrl = `${getFirmwareReleaseNotesUrl(data.firmwareId)}&index=${index}`;
    const res = await fetch(metaUrl, { headers });
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const json = await res.json();
      const presigned =
        json.release_notes?.[index]?.download_url ||
        json.release_notes?.[0]?.download_url ||
        json.download_url ||
        null;
      if (presigned) return { type: "presigned", url: presigned };
      // Fallback: render the JSON as plain text
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: "text/plain" });
      return { type: "blob", url: URL.createObjectURL(blob) };
    }
    const blob = await res.blob();
    return { type: "blob", url: URL.createObjectURL(blob) };
  };

  const handleDownloadReleaseNote = async (index) => {
    setRnLoading(`dl-${index}`);
    try {
      const { type, url: fileUrl } = await resolveReleaseNoteUrl(index);
      if (type === "presigned") {
        // Fetch the actual file from the presigned URL so we can name it correctly
        const fileRes = await fetch(fileUrl);
        if (!fileRes.ok) throw new Error(`File download failed: ${fileRes.status}`);
        const blob = await fileRes.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = releaseNoteFiles[index]?.file_name || `release-note-${index}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      } else {
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = releaseNoteFiles[index]?.file_name || `release-note-${index}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
      }
    } catch (err) {
      console.error("[downloadReleaseNote]", err);
    } finally {
      setRnLoading(null);
    }
  };

  const handleViewReleaseNote = async (index) => {
    // Toggle: clicking the same open file closes the panel
    if (rnViewIndex === index && rnViewUrl) {
      handleCloseRnPanel();
      return;
    }
    setRnLoading(`view-${index}`);
    try {
      const { url: fileUrl } = await resolveReleaseNoteUrl(index);
      if (rnViewUrl && rnViewUrl.startsWith("blob:")) URL.revokeObjectURL(rnViewUrl);
      setRnViewUrl(fileUrl);
      setRnViewName(releaseNoteFiles[index]?.file_name || `Release Note ${index + 1}`);
      setRnViewIndex(index);
    } catch (err) {
      console.error("[viewReleaseNote]", err);
    } finally {
      setRnLoading(null);
    }
  };

  const handleCloseRnPanel = () => {
    if (rnViewUrl && rnViewUrl.startsWith("blob:")) URL.revokeObjectURL(rnViewUrl);
    setRnViewUrl(null);
    setRnViewName(null);
    setRnViewIndex(null);
  };

  // The exact wording the reviewer agrees to. Sent to the backend and stored
  // verbatim on the signature record, so the audit trail holds what was actually
  // shown rather than a second copy that could drift from this one.
  const ATTESTATION_TEXT =
    "I confirm that I have reviewed all firmware details above, verified the SHA-256 hash matches " +
    "the source file, and authorise this baseline for publication to the Tetra fleet.";

  const reviewerLine = (() => {
    const name = user?.name || "Unknown";
    const displayName = name.split(" ")[0] + " " + (name.split(" ")[1]?.[0] || "") + ".";
    const roleLabel = reviewerRoleLabel(role);
    return `I have reviewed and verified the above details \u2014 ${displayName} \u00b7 ${roleLabel}`;
  })();

  const rows = [
    { label: "Version",        value: data.version, bold: true },
    { label: "File Name",      value: data.file?.name || data.fileInfo?.name || uploadedFileName, teal: true },
    { label: "File Size",      value: reviewFileSize(data) },
    { label: "Update Type",    value: null, pill: data.mandatory ? "Mandatory" : "Optional", mandatory: data.mandatory },
    { label: "Min Device FW",  value: data.minFw || "\u2014" },
    { label: "Target Segment", value: data.segment || "\u2014" },
    { label: "Countries",      value: (() => { const codes = data.firmwareRecord?.target_countries || data.countries || []; return codes.length > 0 ? codes.map((c) => { const code = typeof c === "object" ? (c.code || "") : c; const label = COUNTRY_LABEL[code] || (typeof c === "object" ? c.name || code : c); return code ? `${label} (${code})` : label; }).join(", ") : "\u2014"; })() },
    { label: "SHA-256 Hash",   value: data.sha256, mono: true },
  ];

  return (
    <div className={`grid gap-5 items-start ${rnViewUrl ? "grid-cols-[1fr_26rem]" : "grid-cols-1"}`}>
      {/* Left: main content — always full remaining width */}
      <div className="space-y-4 min-w-0">
      {/* Uploaded firmware file banner */}
      {uploadedFileName && (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-0.5">Uploaded Firmware File</p>
            <p className="text-sm font-semibold text-slate-800 truncate">
              {uploadedFileName}
            </p>
          </div>
        </div>
      )}

      {/* Hex viewer modal */}
      {hexContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-4xl flex flex-col" style={{ maxHeight: "85vh" }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-sm font-bold text-slate-800">{data.fileInfo?.name || data.file?.name || "firmware.bin"}</p>
                <p className="text-xs text-slate-400">Hex dump &middot; first 4 KB shown</p>
              </div>
              <button
                onClick={() => setHexContent(null)}
                className="text-slate-400 hover:text-slate-700 text-xl font-bold px-2"
              >&times;</button>
            </div>
            <pre className="overflow-auto flex-1 text-xs font-mono bg-slate-950 text-green-400 p-4 rounded-b-xl">{hexContent}</pre>
          </div>
        </div>
      )}

      {/* Firmware Details card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Firmware Details</span>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map(({ label, value, bold, teal, mono, pill, mandatory: isMandatory }) => (
            <div key={label} className="flex items-start justify-between px-6 py-3 gap-6">
              <span className="text-sm font-medium text-slate-400 shrink-0 w-36">{label}</span>
              {label === "File Name" ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-teal-600 font-medium break-all text-right">{value || "\u2014"}</span>
                  <button
                    type="button"
                    onClick={handleDownloadBin}
                    disabled={downloading || viewing}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#08148C] border border-teal-300 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {downloading ? "..." : "\u2193 Download"}
                  </button>
                  {/* <button
                    type="button"
                    onClick={handleViewBin}
                    disabled={viewing || downloading}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-slate-600 border border-slate-300 bg-white hover:bg-slate-50 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {viewing ? "..." : "\u{1F441} View"}
                  </button> */}
                </div>
              ) : (
              <span className={`text-sm text-right break-all ${reviewValueClass({ bold, teal, mono })}`}>
                {pill ? (
                  <span className={`inline-block text-xs px-3 py-0.5 rounded-full border ${
                    isMandatory
                      ? "border-red-300 text-red-600 bg-red-50"
                      : "border-slate-300 text-slate-600"
                  }`}>{pill}</span>
                ) : (value || "\u2014")}
              </span>
              )}
            </div>
          ))}
        </div>
        {/* Release Notes files — same row style as File Name */}
        {releaseNoteFiles.length > 0 && (
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {releaseNoteFiles.map((f, i) => (
              <div key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className="flex items-center justify-between px-6 py-3 gap-3 min-w-0">
                <span className="text-sm font-medium text-slate-400 shrink-0 w-28">{i === 0 ? "Release Notes" : ""}</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-teal-600 font-medium truncate" title={f.file_name || `File ${i + 1}`}>{f.file_name || `File ${i + 1}`}</span>
                  <button
                    type="button"
                    onClick={() => handleDownloadReleaseNote(i)}
                    disabled={rnLoading !== null}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#08148C] border border-teal-300 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {rnLoading === `dl-${i}` ? "..." : "\u2193 Download"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewReleaseNote(i)}
                    disabled={rnLoading !== null}
                    className={`shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
                      rnViewIndex === i
                        ? "text-[#08148C] border-teal-400 bg-teal-50 hover:bg-teal-100"
                        : "text-slate-600 border-slate-300 bg-white hover:bg-slate-50"
                    }`}
                  >
                    {releaseNoteViewLabel(rnLoading === `view-${i}`, rnViewIndex === i)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signature verified locally from the image's MCUboot TLV trailer at upload time */}
      {data.fwInfo?.signed && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <span className="text-xl">&#x1F510;</span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#08148C]">
              Signature verified &middot; ECDSA P-256
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              {data.fwInfo.keyTrusted
                ? "Signed with this project's expected signing key."
                : "Signed, but the signing key was not recognised."}
              {" "}Image version {data.fwInfo.version}
              {data.fwInfo.securityCounter !== null
                ? ` \u00b7 security counter ${data.fwInfo.securityCounter}`
                : ""}
              {data.fwInfo.encrypted ? " \u00b7 payload encrypted" : ""}
            </p>
          </div>
        </div>
      )}

      {/* Firmware image signing state \u2014 read from the record, never simulated */}
      {artefactSigned && (
        artefactSigned.signed ? (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <span className="text-xl">&#x1F512;</span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-[#08148C]">Firmware image signed</p>
              <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">
                {artefactSigned.signedSha256 || "&#8212;"}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
            <span className="text-xl">&#x1F4E6;</span>
            <div>
              <p className="text-sm font-semibold text-slate-700">Signed by the build pipeline</p>
              <p className="text-xs text-slate-500 mt-0.5">
                The portal does not re-sign the binary. Verify the SHA-256 above matches your build output.
              </p>
            </div>
          </div>
        )
      )}

      {/* SaMD Review Gate */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 space-y-3">
        <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">&#8225; Mandatory Review Gate</p>
        <p className="text-sm text-slate-700">{ATTESTATION_TEXT}</p>
        <p className="text-xs text-slate-500">
          Approving records an electronic signature against this exact binary. The record is append-only and cannot be
          edited or removed afterwards, and the details above can no longer be changed.
        </p>
        <label htmlFor="review-gate-confirm" className={`flex items-center gap-3 ${signature ? "cursor-default" : "cursor-pointer"}`}>
          <input
            id="review-gate-confirm"
            type="checkbox"
            checked={confirmed}
            disabled={!!signature}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="hidden"
          />
          <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${
            confirmed ? "bg-amber-600 border-amber-600" : "border-2 border-amber-400 bg-white"
          }`}>
            {confirmed && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
          </div>
          <span className="text-sm font-semibold text-slate-700">{reviewerLine}</span>
        </label>
      </div>

      {/* What was actually recorded, once the signature exists */}
      {signature && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-4 space-y-1.5">
          <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Approval recorded</p>
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{signature.signer_name}</span>
            {signature.signer_email ? ` (${signature.signer_email})` : ""}
            {signature.signer_role ? ` · ${signature.signer_role}` : ""}
          </p>
          <p className="text-xs text-slate-500">{signature.signed_at}</p>
          <p className="text-xs text-slate-500 font-mono break-all">
            Record #{signature.seq} &middot; {signature.record_hash}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        {/* Once signed, the reviewed details are frozen \u2014 editing metadata after
            approval would leave the signature covering values that changed. */}
        <button
          onClick={onBack}
          disabled={!!signature}
          title={signature ? "Details are locked once the approval has been recorded." : undefined}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500 border border-slate-200 rounded-lg px-4 py-2.5 bg-white transition-colors"
        >
          &#8592; Back
        </button>
        <button
          disabled={!confirmed || proceeding}
          onClick={async () => {
            if (!data?.firmwareId) { setProceedError("Firmware ID missing."); return; }
            setProceeding(true);
            setProceedError(null);
            try {
              // Record the electronic signature first. Only a successful write
              // may advance the wizard \u2014 the publish step depends on it.
              let record = signature;
              if (!record) {
                const result = await approveBaseline(data.firmwareId, ATTESTATION_TEXT);
                record = result?.signature || null;
                setSignature(record);
              }
              const publishDetails = await getFirmwarePublishDetails(data.firmwareId);
              onNext({ ...data, publishDetails, approvalSignature: record });
            } catch (err) {
              setProceedError(err.message || "Failed to record approval.");
            } finally {
              setProceeding(false);
            }
          }}
          className="flex items-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          {proceeding ? "Recording approval..." : "Approve & Continue \u2794"}
        </button>
      </div>
      {proceedError && <p className="text-xs text-red-500 mt-1 text-right">{proceedError}</p>}

      </div>

      {/* Right: release note viewer — fixed-width card, left content keeps its full space */}
      {rnViewUrl && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-sm sticky top-4" style={{ height: "calc(100vh - 10rem)" }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-base leading-none shrink-0">&#x1F4C4;</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{rnViewName}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Release Note &middot; Preview</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCloseRnPanel}
              className="ml-3 shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors text-base font-bold leading-none"
              aria-label="Close viewer"
            >&times;</button>
          </div>
          <iframe
            src={rnViewUrl}
            title={rnViewName}
            className="flex-1 w-full border-0"
          />
        </div>
      )}
    </div>
  );
}

// The tier is a cap on how many devices may be selected when the rollout is
// initiated, expressed as a percentage of the devices in the countries chosen on
// that screen. The exact device count is not known here - the countries have not
// been picked yet - so this screen states the percentage only and the Initiate
// Rollout modal resolves it against the real population.
const SEGMENTS = [
  {
    id: "canary",
    emoji: "🌱",
    label: "Canary",
    desc: "Initial rollout — monitor failure rate before expanding",
    devices: "Up to 5% of the selected countries' devices",
    pct: 5,
  },
  {
    id: "extended",
    emoji: "🔵",
    label: "Extended",
    desc: "Wider rollout — available once the canary tier has reported clean",
    devices: "Up to 25% of the selected countries' devices",
    pct: 25,
  },
  {
    id: "full",
    emoji: "🌐",
    label: "Full Fleet",
    desc: "All eligible devices — requires the earlier tiers to have reported clean",
    devices: "Up to 100% of the selected countries' devices",
    pct: 100,
  },
];

function StepPublish({ data, onBack, onDone, onPublish }) {
  const [selectedSeg, setSelectedSeg] = useState("canary");
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [apiSummary, setApiSummary] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const summaryRef = useRef(null);
  const { toast } = useToast();

  const seg = SEGMENTS.find((s) => s.id === selectedSeg);

  const publishDetails = data.publishDetails || {};

  // Only rows backed by real data. The previous Notification / Rollout Pauses If /
  // CloudFront URL TTL rows were bound to attributes no service has ever written,
  // so they rendered "—" permanently while implying the platform had been
  // configured for them.
  const configRows = [
    { label: "Version",      value: publishDetails.version_string || data.version || "—" },
    { label: "Segment",      value: publishDetails.target_segment || "—" },
    { label: "Rollout Tier", value: seg ? `${seg.label} — ${seg.devices.toLowerCase()}` : "—" },
  ];

  const handleDownloadSummary = () => {
    const raw = apiSummary ?? {};
    const d = raw.data ?? raw.firmware ?? raw;
    const tsVal = d.published_at ?? d.publishedAt ?? d.uploaded_at ?? d.uploadedAt ?? d.created_at ?? d.createdAt;
    const parseTs = (v) => {
      if (!v) return "—";
      const ms = toEpochMs(v);
      return isNaN(ms) ? String(v) : new Date(ms).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" });
    };
    const publishedAt = parseTs(tsVal);
    const version = publishDetails.version_string || data.version || "baseline";

    const uploadedAtPdf = parseTs(d.uploaded_at ?? d.uploadedAt);
    const rows = [
      ["Firmware ID",          d.id || data.firmwareId || "—"],
      ["Version",              d.version_string || "—"],
      ["File Name",            d.file_name || "—"],
      ["File Size",            d.file_size ? formatPdfFileSize(d.file_size) : "—"],
      ["Status",               d.status ? d.status.charAt(0).toUpperCase() + d.status.slice(1) : "—"],
      ["Is Mandatory",         yesNoOr(d.is_mandatory, "—")],
      ["Min Device FW",        d.min_device_fw_required || "—"],
      ["Target Countries",     formatTargetCountries(d.target_countries ?? d.country)],
      ["Segment",              d.target_segment || "—"],
      ["Rollout Tier",         tierLabel(d.rollout_tier)],
      ["Encrypted ID",         d.encrypted_id || "—"],
      ["SHA-256",              d.sha256 || "—"],
      ["Uploaded At",          uploadedAtPdf || "—"],
      ["Published At",         publishedAt || "—"],
    ];

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const margin = 48;
    let y = 52;

    // Header bar
    doc.setFillColor(8, 20, 140);
    doc.rect(margin, y, W - margin * 2, 42, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("Baseline Published — Publish Summary", margin + 16, y + 26);
    y += 58;

    // Sub-header
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Tetra Micro Infusion System  ·  Fleet Management Portal  ·  Generated ${publishedAt || new Date().toLocaleString()}`, margin, y);
    y += 24;

    // Section label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text("PUBLISH DETAILS", margin, y);
    y += 10;

    // Table header row
    const col1 = margin;
    const col2 = margin + 180;
    const rowH = 28;

    doc.setFillColor(248, 250, 252);
    doc.rect(col1, y, W - margin * 2, rowH, "F");
    doc.setDrawColor(226, 232, 240);
    doc.rect(col1, y, W - margin * 2, rowH, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text("FIELD", col1 + 10, y + 18);
    doc.text("VALUE", col2 + 10, y + 18);
    y += rowH;

    // Table rows
    const lineH = 14;
    const rowPadV = 10;
    rows.forEach(([label, value], i) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const maxW = W - margin * 2 - 190;
      const lines = doc.splitTextToSize(String(value), maxW);
      const dynamicRowH = Math.max(28, lines.length * lineH + rowPadV * 2);

      const bg = i % 2 === 0 ? [255, 255, 255] : [248, 250, 252];
      doc.setFillColor(...bg);
      doc.rect(col1, y, W - margin * 2, dynamicRowH, "F");
      doc.setDrawColor(241, 245, 249);
      doc.rect(col1, y, W - margin * 2, dynamicRowH, "S");

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(label, col1 + 10, y + rowPadV + lineH * 0.75);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(lines, col2 + 10, y + rowPadV + lineH * 0.75);
      y += dynamicRowH;
    });

    y += 24;

    doc.save(`publish-summary-${version}.pdf`);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const firmwareId = publishDetails.id || data.firmwareId;
      if (!firmwareId) throw new Error("Firmware ID missing.");
      // The tier travels with the publish: it caps how many devices may be
      // selected when the rollout is initiated. Enforced server-side.
      await finalPublishFirmware(firmwareId, selectedSeg);
      setDone(true);
      onPublish?.();
    } catch (err) {
      toast({ title: "Publish failed", description: err.message || "Could not publish the baseline.", variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  if (done && showSummary) {
    const raw = apiSummary ?? {};
    const d = raw.data ?? raw.firmware ?? raw;
    const tsVal = d.published_at ?? d.publishedAt ?? d.uploaded_at ?? d.uploadedAt ?? d.created_at ?? d.createdAt;
    const parseTs = (v) => {
      if (!v) return null;
      const ms = toEpochMs(v);
      return isNaN(ms) ? String(v) : new Date(ms).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" });
    };
    const publishedAt = parseTs(tsVal);
    const uploadedAt  = parseTs(d.uploaded_at ?? d.uploadedAt);
    const summaryRows = [
      { label: "Firmware ID",            value: d.id || data.firmwareId || null },
      { label: "Version",                value: d.version_string || null },
      { label: "File Name",              value: d.file_name || null },
      { label: "File Size",              value: d.file_size ? formatPdfFileSize(d.file_size) : null },
      { label: "Status",                 value: d.status ? d.status.charAt(0).toUpperCase() + d.status.slice(1) : null },
      { label: "Is Mandatory",           value: yesNoOr(d.is_mandatory, null) },
      { label: "Min Device FW",          value: d.min_device_fw_required || null },
      { label: "Target Countries",       value: formatTargetCountries(d.target_countries ?? d.country, null) },
      { label: "Segment",                value: d.target_segment || null },
      { label: "Rollout Tier",           value: tierLabel(d.rollout_tier, null) },
      { label: "Encrypted ID",           value: d.encrypted_id || null, mono: true },
      { label: "SHA-256",                value: d.sha256 || null, mono: true },
      { label: "Uploaded At",            value: uploadedAt },
      { label: "Published At",           value: publishedAt },
    ];
    return (
      <div className="fixed inset-0 z-50 bg-white overflow-auto flex flex-col items-center justify-start px-6 py-12" ref={summaryRef}>
        <div className="w-full max-w-2xl space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Publish Summary</h2>
              <p className="text-xs text-slate-400">Generated {publishedAt}</p>
            </div>
            <div className="ml-auto">
              <button
                onClick={onDone}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#08148C] hover:bg-[#060f6e] px-4 py-2 rounded-lg transition-colors"
              >
                &#8592; Back to Baseline view
              </button>
            </div>
          </div>

          {/* Summary card */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Publish Details</span>
            </div>
            <div className="divide-y divide-slate-100">
              {summaryRows.map(({ label, value, mono }) => (
                <div key={label} className="flex items-start justify-between px-6 py-3 gap-6">
                  <span className="text-sm font-medium text-slate-400 shrink-0 w-44">{label}</span>
                  <span className={`text-sm text-right break-all ${mono ? "font-mono text-teal-600 text-xs" : "text-slate-800 font-medium"}`}>
                    {value ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={handleDownloadSummary}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#08148C] border border-teal-300 bg-teal-50 hover:bg-teal-100 px-4 py-2 rounded-lg transition-colors"
            >
              &#8595; Download Summary
            </button>
            <button
              onClick={onDone}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#08148C] hover:bg-[#060f6e] px-5 py-2 rounded-lg transition-colors"
            >
              &#8592; Back to Baseline view
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Baseline Published!</h2>
        <p className="text-sm text-slate-500">
          Version <strong>{data.version}</strong> published to the{" "}
          <strong>{seg.label}</strong> segment.
        </p>
        <div className="flex items-center gap-3 mt-4">
          <button
            disabled={detailsLoading}
            onClick={async () => {
              const firmwareId = publishDetails.id || data.firmwareId;
              if (!firmwareId) return;
              setDetailsLoading(true);
              setDetailsError(null);
              try {
                const res = await getBaseline(firmwareId);
                setApiSummary(res);
                setShowSummary(true);
              } catch (err) {
                setDetailsError(err.message || "Failed to load details.");
              } finally {
                setDetailsLoading(false);
              }
            }}
            className="border border-teal-300 text-[#08148C] bg-teal-50 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
          >
            {detailsLoading ? "Loading..." : "View Details"}
          </button>
          {detailsError && <p className="text-xs text-red-500 mt-1">{detailsError}</p>}
          <button
            onClick={onDone}
            className="bg-[#08148C] hover:bg-[#060f6e] text-white text-sm font-semibold px-8 py-2.5 rounded-lg transition-colors"
          >
            Back to Baseline view
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Segment selector cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {SEGMENTS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelectedSeg(s.id)}
            className={`text-left rounded-xl border-2 px-4 py-4 transition-colors ${
              selectedSeg === s.id
                ? "border-teal-500 bg-teal-50"
                : "border-slate-200 bg-white hover:border-teal-300"
            }`}
          >
            <p className="text-sm font-bold text-slate-800 mb-1">
              <span className="mr-1.5">{s.emoji}</span>{s.label}
            </p>
            <p className="text-xs text-slate-500 mb-3 leading-snug">{s.desc}</p>
            <p className="text-xs font-semibold text-[#08148C]">{s.devices}</p>
          </button>
        ))}
      </div>

      {/* Publish configuration */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Publish Configuration</span>
        </div>
        <div className="divide-y divide-slate-100">
          {configRows.map(({ label, value, mono }) => (
            <div key={label} className="flex items-start justify-between px-6 py-3 gap-6">
              <span className="text-sm font-medium text-slate-400 shrink-0 w-40">{label}</span>
              <span className={`text-sm text-right break-all ${mono ? "font-mono text-teal-600 text-xs" : "text-slate-700"}`}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <span className="text-blue-500 text-lg leading-none mt-0.5">&#x2139;</span>
        <div>
          <p className="text-sm font-semibold text-slate-800">Tiered rollout &#8212; expansion is gated on reported outcomes</p>
          <p className="text-xs text-slate-500 mt-0.5">
            The tier caps how many devices may be selected when the rollout is initiated. Expanding to a wider tier
            is refused until the current tier&#39;s devices have reported and the failure rate is under threshold.
            This action is final and immutably logged.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        {/* Reaching this step means the approval is already recorded, so the
            reviewed details are frozen. */}
        <button
          onClick={onBack}
          disabled={publishing || !!data?.approvalSignature}
          title={data?.approvalSignature ? "Details are locked once the approval has been recorded." : undefined}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2.5 bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          &#8592; Back
        </button>
        <button
          disabled={publishing}
          onClick={handlePublish}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
        >
          &#x1F680; Publish to {seg.label} Segment
        </button>
      </div>
    </div>
  );
}

// ── Draft persistence helpers ─────────────────────────────────────────
const DRAFT_PREFIX = "emed_fw_draft_";

function loadDraft(firmwareId) {
  if (!firmwareId) return null;
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + firmwareId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveDraft(firmwareId, data) {
  if (!firmwareId) return;
  try {
    // File objects cannot be serialised — strip them. Base64 blobs are kept so files
    // can be fully restored when the user resumes the draft without re-uploading.
    localStorage.setItem(
      DRAFT_PREFIX + firmwareId,
      JSON.stringify(
        { ...data, savedAt: new Date().toISOString() },
        (k, v) => v instanceof File ? undefined : v
      )
    );
  } catch {}
}

function clearDraft(firmwareId) {
  if (!firmwareId) return;
  try { localStorage.removeItem(DRAFT_PREFIX + firmwareId); } catch {}
}

function formatDraftTime(date) {
  if (!date) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}
// ──────────────────────────────────────────────────────────────────────


// Help line under the country picker. Hoisted so the count/pluralisation
// ternary is not nested inside the error-vs-hint ternary (S3358).

// File size for the generated PDFs. One module-level function rather than two
// byte-identical local copies (S4144), each of which also needed its early
// return braced (S2681).
function formatPdfFileSize(v) {
  const b = parseInt(v, 10);
  if (isNaN(b)) {
    return v || "—";
  }
  return b >= 1048576 ? (b / 1048576).toFixed(2) + " MB" : (b / 1024).toFixed(1) + " KB";
}

function countriesHelpText(countries) {
  if (countries.length === 0) return "Select all countries this firmware is approved for.";
  const noun = countries.length === 1 ? "country" : "countries";
  return `${countries.length} ${noun} selected`;
}

export default function UploadBaselinePage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const draftId = new URLSearchParams(search).get("draftId");

  // Priority: localStorage draft (cross-session resume) > sessionStorage (in-session refresh)
  // If no draftId is present this is a fresh "New Baseline" navigation — always start clean.
  const [step, setStep] = useState(() => {
    if (draftId) {
      const draft = loadDraft(draftId);
      if (draft?.step) {
        // If the draft is at step 1 but the file was already uploaded (firmwareId exists),
        // resume at step 2 — the user may have clicked Back from step 2 before navigating away.
        if (draft.step === 1 && draft.firmwareId) return 2;
        return draft.step;
      }
    }
    // No draftId — fresh session, clear any stale sessionStorage
    try {
      sessionStorage.removeItem("uploadWizard_step");
      sessionStorage.removeItem("uploadWizard_data");
    } catch {}
    return 1;
  });

  const [wizardData, setWizardData] = useState(() => {
    if (draftId) {
      const draft = loadDraft(draftId);
      if (draft) return draft;
    }
    // No draftId — return empty (sessionStorage was already cleared above)
    return {};
  });

  const [published, setPublished] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(() => {
    if (draftId) {
      const draft = loadDraft(draftId);
      return draft?.savedAt ? new Date(draft.savedAt) : null;
    }
    return null;
  });

  // Keep a stable ref to the current firmwareId so callbacks can read it without staling
  const firmwareIdRef = useRef(wizardData.firmwareId || draftId || null);
  useEffect(() => {
    firmwareIdRef.current = wizardData.firmwareId || draftId || null;
  }, [wizardData.firmwareId, draftId]);

  const updateWizardData = (data) => {
    setWizardData(data);
    const fwId = data.firmwareId || firmwareIdRef.current;
    // Update ref immediately so updateStep (called right after) can use it
    if (fwId) firmwareIdRef.current = fwId;
    try {
      sessionStorage.setItem("uploadWizard_data", JSON.stringify(data, (k, v) => v instanceof File ? undefined : v));
    } catch {}
    // Persist to localStorage so draft survives navigation / tab close
    saveDraft(fwId, { ...data, step });
    setDraftSavedAt(new Date());
  };

  const updateStep = (s) => {
    setStep(s);
    try { sessionStorage.setItem("uploadWizard_step", String(s)); } catch {}
    // Only update the step field in the existing draft — avoids overwriting wizardData
    // with stale closure state when updateStep is called right after updateWizardData.
    const fwId = firmwareIdRef.current;
    if (fwId) {
      const existing = loadDraft(fwId);
      if (existing) {
        saveDraft(fwId, { ...existing, step: s });
      }
    }
  };

  const clearWizard = () => {
    clearDraft(firmwareIdRef.current);
    setStep(1);
    setWizardData({});
    try {
      sessionStorage.removeItem("uploadWizard_step");
      sessionStorage.removeItem("uploadWizard_data");
    } catch {}
  };

  const subtitles = [
    "Upload the signed firmware binary file (.bin)",
    "Enter version, release notes and update type",
    "Verify all details and record your approval \u00b7 SaMD mandatory review gate",
    "Select rollout tier \u00b7 Caps how many devices may be targeted when the rollout is initiated",
  ];

  return (
    <div className={step === 3 ? "max-w-full" : "max-w-3xl"}>
      <div className="flex items-center gap-5 mb-2">
        {!(step === 4 && published) && (
          <button
            onClick={() => step === 1 ? navigate("/firmware") : updateStep(step - 1)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors shrink-0"
          >
            &#8592; Back
          </button>
        )}
        <h1 className="text-2xl font-bold text-slate-900">
          {step === 1 && "New Baseline \u2014 Upload Firmware"}
          {step === 2 && "New Baseline \u2014 Metadata"}
          {step === 3 && "Review & Sign"}
          {step === 4 && `Publish ${wizardData.version || ""}`}
        </h1>
        {draftSavedAt && step < 4 && !published && (
          <span className="ml-auto shrink-0 flex items-center gap-1.5 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            Draft saved {formatDraftTime(draftSavedAt)}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-400 mb-8">
        Step {step} of 4 &middot; {subtitles[step - 1]}
      </p>

      <StepIndicator current={step} />

      {step === 1 && (
        <StepUpload
          initialFile={wizardData.file}
          initialSha256={wizardData.sha256}
          initialFileInfo={wizardData.fileInfo}
          initialFirmwareId={wizardData.firmwareId}
          initialBuildDate={wizardData.buildDate}
          initialBuildTime={wizardData.buildTime}
          onNext={(data) => {
            const autosaved = loadDraft(wizardData.firmwareId || firmwareIdRef.current);
            updateWizardData(mergeUploadIntoWizard(wizardData, data, autosaved));
            updateStep(2);
          }}
        />
      )}
      {step === 2 && (
        <StepMetadata
          uploadData={wizardData}
          initialData={wizardData}
          onBack={() => updateStep(1)}
          onNext={(data) => { updateWizardData(data); updateStep(3); }}
          onAutosave={(fields) => {
            const fwId = wizardData.firmwareId || firmwareIdRef.current;
            saveDraft(fwId, { ...wizardData, ...fields, step: 2 });
            setDraftSavedAt(new Date());
          }}
        />
      )}
      {step === 3 && (
        <StepReview
          data={wizardData}
          onBack={() => updateStep(2)}
          onNext={(data) => { updateWizardData(data); updateStep(4); }}
        />
      )}
      {step === 4 && (
        <StepPublish
          data={wizardData}
          onBack={() => updateStep(3)}
          onPublish={() => setPublished(true)}
          onDone={() => { clearWizard(); navigate("/firmware"); }}
        />
      )}
    </div>
  );
}